import {createListenerMiddleware} from '@reduxjs/toolkit';
import SegmentUtils from '@spothero/utils/segment';
import toNumber from 'lodash/toNumber';
import {
    getInitialFacilities,
    setFilteredFacilities,
} from 'pages/facilities/store/facilities-actions';

export const analyticsMiddleware = createListenerMiddleware();

/**
 * Middleware to listen for the fulfillment of the `getInitialFacilities` asynchronous action.
 * When the action is fulfilled, it identifies the user and sets user traits using SegmentUtils.
 *
 * @param {Object} options - The options for the listener middleware.
 * @param {Object} options.actionCreator - The action creator to listen for.
 * @param {Function} options.effect - The effect function to call when the action is fulfilled.
 */
analyticsMiddleware.startListening({
    actionCreator: getInitialFacilities.fulfilled,
    effect: (action, listenerApi) => {
        const {
            user: {
                data: {user_id: userId, email},
            },
            facilities: {
                meta: {totalFacilityCount},
            },
        } = listenerApi.getState();

        SegmentUtils.identify({
            uniqueId: userId,
            traits: {
                email,
                'Facility Count': totalFacilityCount,
            },
        });
    },
});

/**
 * Starts listening for the `setFilteredFacilities.fulfilled` action and triggers the defined effect.
 *
 * When the action is fulfilled, the provided effect function is executed. The effect function extracts
 * the user segment and the list of filtered facilities from the state. It then maps the facility IDs
 * to numbers and sends a tracking event to the Segment analytics service with details about the filtered facilities.
 *
 * @function startListening
 * @memberof analyticsMiddleware
 * @param {Object} config - Configuration object for the listener
 * @param {Function} config.actionCreator - Action creator to listen for. In this case, `setFilteredFacilities.fulfilled`.
 * @param {Function} config.effect - Effect to execute when the action is dispatched.
 * @param {Object} action - Dispatched action object.
 * @param {Function} listenerApi - API for accessing the current state and dispatching additional actions.
 * @see {@link createListenerMiddleware} for more details on listener middleware.
 *
 */
analyticsMiddleware.startListening({
    actionCreator: setFilteredFacilities.fulfilled,
    effect: (action, listenerApi) => {
        const {
            user: {segment},
            facilities: {filteredFacilities},
        } = listenerApi.getState();

        // Todo: handle type casting upstream at the store level
        const filteredFacilityIds = filteredFacilities.ids.map(toNumber);

        SegmentUtils.track({
            event: 'Filtered Facilities',
            properties: {
                'Facility IDs': filteredFacilityIds,
                'Filtered Facility Count': filteredFacilities.ids.length,
                user: segment,
            },
        });
    },
});

/**
 * Starts listening for `@@router/LOCATION_CHANGE` actions to track user analytics.
 *
 * This middleware listens for location changes in the router and triggers an analytics
 * event to identify the user through SegmentUtils.
 *
 * @param {object} options - The options for the middleware.
 * @param {string} options.type - The type of action to listen for.
 * @param {function} options.effect - The effect function to execute when the action is dispatched.
 * @param {object} action - The dispatched action.
 * @param {object} listenerApi - The listener API providing access to the state.
 * @param {function} listenerApi.getState - Function to get the current state.
 *
 */
analyticsMiddleware.startListening({
    type: '@@router/LOCATION_CHANGE',
    effect: (action, listenerApi) => {
        const {
            user: {
                data: {user_id: userId, email},
            },
        } = listenerApi.getState();

        SegmentUtils.identify({
            uniqueId: userId,
            traits: {
                email,
            },
        });
    },
});
